import React, { useEffect, useState } from 'react';
import { Alert, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'pwa_install_dismissed';

const PwaInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, '1');
  };

  if (!visible) return null;

  return (
    <Alert
      severity="info"
      icon={<GetAppIcon />}
      action={
        <>
          <Button color="inherit" size="small" onClick={install} sx={{ fontWeight: 700, mr: 0.5 }}>
            Install
          </Button>
          <IconButton color="inherit" size="small" onClick={dismiss}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      }
      sx={{
        position: 'fixed',
        bottom: { xs: 72, md: 16 },
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        width: { xs: 'calc(100% - 32px)', sm: 480 },
        boxShadow: '0 8px 32px rgba(7,24,45,0.15)',
      }}
    >
      Add RideSaathi to your home screen for quick access.
    </Alert>
  );
};

export default PwaInstallPrompt;
